import React, { useState, useRef, useCallback } from 'react';
import { Row, Col, Modal, ModalBody, Button } from 'reactstrap';

// Importar todas las imágenes - solo las JPG por rendimiento
const importAll = (r) => {
  let images = {};
  r.keys().map((item, index) => {
    images[item.replace('./', '')] = r(item);
    return item;
  });
  return images;
};

// Importar solo las imágenes JPG (excluir videos y HEIF)
const images = importAll(
  require.context('../../assets/img/aparatos-ortopedia/', false, /\.(jpg|jpeg)$/i)
);

// Componente de imagen individual con lazy loading
const LazyImage = ({ src, alt, onClick, index }) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef();

  const handleIntersection = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
      setInView(true);
    }
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: '50px'
    });
    
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => observer.disconnect();
  }, [handleIntersection]);

  return (
    <div 
      ref={imgRef}
      style={{
        width: '100%',
        height: '250px',
        borderRadius: 15,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={onClick}
    >
      {!inView ? (
        // Placeholder mientras no está en vista
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: '#6c757d'
        }}>
          <div style={{fontSize: '2rem', marginBottom: 10}}>🦷</div>
          <small>Cargando...</small>
        </div>
      ) : (
        <>
          {!loaded && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: '#6c757d'
            }}>
              <div style={{fontSize: '2rem', marginBottom: 10}}>🦷</div>
              <small>Cargando imagen...</small>
            </div>
          )}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'scale(1)' : 'scale(1.1)'
            }}
            onLoad={() => setLoaded(true)}
            onMouseEnter={(e) => {
              if (loaded) e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              if (loaded) e.target.style.transform = 'scale(1)';
            }}
          />
        </>
      )}
    </div>
  );
};

const ImageGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const imagesPerPage = 9;
  
  const imageArray = Object.entries(images).map(([key, src]) => ({
    src,
    alt: `Aparato de ortopedia ${key.replace(/\.(jpg|jpeg)$/i, '')}`,
    filename: key
  }));

  const totalPages = Math.ceil(imageArray.length / imagesPerPage);
  const currentImages = imageArray.slice(
    currentPage * imagesPerPage,
    (currentPage + 1) * imagesPerPage
  );

  const openModal = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(currentPage * imagesPerPage + index);
    setShowModal(true);
    // Prevenir scroll del body cuando modal está abierto
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.width = '100%';
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedImage(null);
    // Restaurar scroll del body y posición
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.overflow = '';
    document.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % imageArray.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(imageArray[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = currentIndex === 0 ? imageArray.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedImage(imageArray[prevIndex]);
  };

  const handleKeyPress = useCallback((e) => {
    if (!showModal) return;
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'Escape') closeModal();
  }, [showModal, currentIndex]);

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  return (
    <>
      {/* Información de la galería */}
      <div className="text-center mb-4">
        <h4 style={{color: '#32325d', marginBottom: 10, fontWeight: 600}}>
          Galería de Aparatos ({imageArray.length} diseños)
        </h4>
        <p style={{color: '#32325d', fontSize: '1rem', fontWeight: 500}}>
          Haz clic en cualquier imagen para verla en tamaño completo
        </p>
      </div>

      {/* Controles de paginación superior */}
      <div className="text-center mb-4">
        <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
          <Button
            style={{
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: currentPage === 0 ? '#e9ecef' : '#259778',
              borderColor: currentPage === 0 ? '#e9ecef' : '#259778',
              color: currentPage === 0 ? '#6c757d' : '#ffffff'
            }}
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ←
          </Button>
          
          <span style={{
            color: '#32325d',
            fontWeight: 600,
            fontSize: '1rem',
            minWidth: '120px'
          }}>
            Página {currentPage + 1} de {totalPages}
          </span>
          
          <Button
            style={{
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: currentPage === totalPages - 1 ? '#e9ecef' : '#259778',
              borderColor: currentPage === totalPages - 1 ? '#e9ecef' : '#259778',
              color: currentPage === totalPages - 1 ? '#6c757d' : '#ffffff'
            }}
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            →
          </Button>
        </div>
        
        {/* Indicadores de páginas */}
        <div className="d-flex justify-content-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i)}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: i === currentPage ? '#259778' : '#e9ecef',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            />
          ))}
        </div>
      </div>

      {/* Grid de imágenes - Solo página actual */}
      <Row>
        {currentImages.map((image, index) => (
          <Col lg="3" md="4" sm="6" xs="12" key={image.filename} className="mb-4">
            <LazyImage
              src={image.src}
              alt={image.alt}
              index={index}
              onClick={() => openModal(image, index)}
            />
          </Col>
        ))}
      </Row>

      {/* Controles de paginación inferior */}
      <div className="text-center mt-4">
        <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
          <Button
            style={{
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: currentPage === 0 ? '#e9ecef' : '#259778',
              borderColor: currentPage === 0 ? '#e9ecef' : '#259778',
              color: currentPage === 0 ? '#6c757d' : '#ffffff'
            }}
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ←
          </Button>
          
          <span style={{
            color: '#32325d',
            fontWeight: 600,
            fontSize: '1rem',
            minWidth: '120px'
          }}>
            Página {currentPage + 1} de {totalPages}
          </span>
          
          <Button
            style={{
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: currentPage === totalPages - 1 ? '#e9ecef' : '#259778',
              borderColor: currentPage === totalPages - 1 ? '#e9ecef' : '#259778',
              color: currentPage === totalPages - 1 ? '#6c757d' : '#ffffff'
            }}
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            →
          </Button>
        </div>
        
        {/* Indicadores de páginas */}
        <div className="d-flex justify-content-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i)}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: i === currentPage ? '#259778' : '#e9ecef',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            />
          ))}
        </div>
      </div>

      {/* Modal para vista ampliada */}
      <Modal 
        isOpen={showModal} 
        toggle={closeModal} 
        size="lg"
        backdrop="static"
        style={{
          maxWidth: '90vw',
          margin: '0 auto',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1050
        }}
        contentClassName="border-0"
        modalClassName="position-fixed"
      >
        <ModalBody style={{padding: 0, position: 'relative'}}>
          {selectedImage && (
            <>
              {/* Imagen principal */}
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                style={{
                  width: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  backgroundColor: '#000'
                }}
              />
              
              {/* Controles de navegación */}
              <Button
                color="light"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '10px',
                  transform: 'translateY(-50%)',
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.8
                }}
                onClick={prevImage}
              >
                ←
              </Button>
              
              <Button
                color="light"
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '10px',
                  transform: 'translateY(-50%)',
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.8
                }}
                onClick={nextImage}
              >
                →
              </Button>
              
              {/* Botón cerrar */}
              <Button
                color="light"
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.8
                }}
                onClick={closeModal}
              >
                ×
              </Button>
              
              {/* Contador de imágenes */}
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '5px 15px',
                borderRadius: '20px',
                fontSize: '0.9rem'
              }}>
                {currentIndex + 1} de {imageArray.length}
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ImageGallery;